// React

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

import {
  CircularProgress,
  Collapse,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";
import { validarName, validateDNI, validateEmail, validatePhone } from "utils/validations";
import ModalVerificar from "../ModalVerificar";

function CrearCuenta({ handleCancel }) {
  const [usuario, setUsuario] = useState();
  const [openVerificar, setOpenVerificar] = useState(false);
  const { handleSnackbar } = useContext(MessageManager);
  const [openModalPlan, setOpenModalPlan] = useState(false);
  const [selectedCarrera, setSelectedCarrera] = useState(null);
  const [sedes, setSedes] = useState([]);

  const handleChanges = (e) => {
    setUsuario({
      ...usuario,
      [e.target.name]: e.target.value,
    });
  };

  const { data: paises } = useQuery(
    gql`
      query getPaises {
        paises {
          id
          nombre
          nacionalidad
          codigo_internacional
        }
      }
    `
  );

  const { data: provincias } = useQuery(gql`
    query GetProvincias {
      provincias {
        id
        nombre
      }
    }
  `);

  const [getLocalidades, { data: localidades }] = useLazyQuery(gql`
    query GetLocalidades($provincia: ID!) {
      localidades(provincia: $provincia) {
        id
        nombre
        provincia {
          id
        }
      }
    }
  `);

  const { data: carreras } = useQuery(
    gql`
      query getCarreras {
        carrerasPublic(filter: { tipo: "Carrera" }) {
          id
          nombre
          sedes {
            id
            nombre
          }
        }
      }
    `
  );

  const [sendVerificacion, { loading: loadingVerificacion }] = useMutation(
    gql`
      mutation sendVerificationEmail($email: String!) {
        sendVerificationEmail(email: $email)
      }
    `
  );

  useEffect(() => {
    if (carreras?.carrerasPublic) {
      const sedes = carreras.carrerasPublic.reduce((acc, carrera) => {
        if (carrera.sedes) {
          carrera.sedes.forEach((sede) => {
            if (!acc.find((s) => s.id === sede.id)) {
              acc.push(sede);
            }
          });
        }
        return acc;
      }, []);
      setSedes(sedes);
    }
  }, [carreras]);

  useEffect(() => {
    if (usuario?.idProvincia) {
      getLocalidades({ variables: { provincia: usuario?.idProvincia } }).then((data) => {
        if (!data?.data?.localidades?.find((localidad) => localidad.id === usuario?.idLocalidad)) {
          let userNew = { ...usuario };
          delete userNew.idLocalidad;
          setUsuario(userNew);
        }
      });
    }
  }, [usuario?.idProvincia]);

  return (
    <SoftBox p={3}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center">
        <SoftBox>
          <SoftTypography variant="h6">Crea tu cuenta</SoftTypography>
        </SoftBox>
        <SoftBox display={{ xs: "block", sm: "none" }}>
          <SoftButton
            color="primary"
            
            onClick={handleCancel}
            fullWidth={{
              xs: true,
              sm: false,
            }}
            disabled={loadingVerificacion}
            iconOnly
          >
            <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
          </SoftButton>
        </SoftBox>
      </SoftBox>
      <SoftBox py={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputLabel htmlFor="username">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Email de usuario
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="email"
              placeholder="Ingrese el email de usuario"
              type="text"
              name="email"
              value={usuario?.email || ""}
              error={usuario?.email?.length > 0 && !validateEmail(usuario?.email)}
              onChange={handleChanges}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="nombre">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Nombre
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="nombre"
              placeholder="Ingrese el nombre del usuario"
              type="text"
              name="nombre"
              value={usuario?.nombre || ""}
              error={usuario?.nombre?.length > 0 && !validarName(usuario?.nombre)}
              onChange={handleChanges}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="apellido">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Apellido
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="apellido"
              placeholder="Ingrese el apellido del usuario"
              type="text"
              name="apellido"
              value={usuario?.apellido || ""}
              error={usuario?.apellido?.length > 0 && !validarName(usuario?.apellido)}
              onChange={handleChanges}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="dni">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                DNI
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="dni"
              placeholder="Ingrese el dni del usuario"
              type="text"
              name="dni"
              value={usuario?.dni || ""}
              error={usuario?.dni?.length > 0 && !validateDNI(usuario?.dni)}
              onChange={handleChanges}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="fechaNacimiento">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Fecha de nacimiento
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="fechaNacimiento"
              type="date"
              name="fechaNacimiento"
              value={usuario?.fechaNacimiento || ""}
              error={
                usuario?.fechaNacimiento &&
                (dayjs(usuario?.fechaNacimiento).isAfter(dayjs().subtract(17, "year")) ||
                  dayjs(usuario?.fechaNacimiento).isBefore(dayjs().subtract(100, "year")))
              }
              onChange={handleChanges}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              "& .MuiInputBase-root": {
                maxHeight: "40px",
                paddingRight: "40px !important",
              },
            }}
          >
            <InputLabel htmlFor="nacionalidad">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Nacionalidad
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <Select
              name="idPais"
              value={usuario?.idPais || usuario?.pais?.id || -1}
              onChange={handleChanges}
            >
              <MenuItem value={-1} disabled>
                Seleccione uno
              </MenuItem>
              {paises?.paises?.map((pais) => (
                <MenuItem key={pais.id} value={pais.id}>
                  <SoftBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      width: "100%",
                    }}
                  >
                    <SoftBox>
                      <SoftTypography variant="p">{pais.nacionalidad}</SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                      <SoftBox
                        component="img"
                        src={`https://flagsapi.com/${pais.codigo_internacional}/flat/64.png`}
                        alt={`${pais.nombre}`}
                        sx={{
                          height: "25px",
                        }}
                      />
                    </SoftBox>
                  </SoftBox>
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="genero">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Genero
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <Select name="genero" value={usuario?.genero || -1} onChange={handleChanges}>
              <MenuItem value={-1} disabled>
                Seleccione uno
              </MenuItem>
              <MenuItem value={"Masculino"}>Masculino</MenuItem>
              <MenuItem value={"Femenino"}>Femenino</MenuItem>
              <MenuItem value={"X"}>X</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="provincia">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Provincia de residencia
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <Select
              name="provincia"
              value={usuario?.idProvincia || -1}
              onChange={(e) =>
                setUsuario({
                  ...usuario,
                  idProvincia: e.target.value,
                  provincia: provincias?.provincias.find(
                    (provincia) => provincia.id === e.target.value
                  ),
                })
              }
            >
              <MenuItem value={-1} disabled>
                Seleccione uno
              </MenuItem>
              {provincias?.provincias?.map((provincia) => (
                <MenuItem key={provincia.id} value={provincia.id}>
                  {provincia.nombre}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="localidad">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Localidad de residencia
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <Select
              name="localidad"
              value={usuario?.idLocalidad || -1}
              onChange={(e) =>
                setUsuario({
                  ...usuario,
                  idLocalidad: e.target.value,
                  localidad: localidades?.localidades.find(
                    (localidad) => localidad.id === e.target.value
                  ),
                })
              }
            >
              <MenuItem value={-1} disabled>
                {usuario?.idProvincia ? "Seleccione una" : "Seleccione una provincia"}
              </MenuItem>
              {localidades?.localidades?.map((localidad) => (
                <MenuItem key={localidad.id} value={localidad.id}>
                  {localidad.nombre}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="telefono">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Teléfono de contacto
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  &nbsp;*
                </SoftTypography>
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="telefono"
              placeholder="Código de área + número"
              type="text"
              name="telefono"
              value={usuario?.telefono || ""}
              error={usuario?.telefono?.length > 0 && !validatePhone(usuario?.telefono)}
              onChange={handleChanges}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="telefonoRespaldo">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Teléfono de contacto de respaldo
              </SoftTypography>
            </InputLabel>
            <SoftInput
              label="telefonoRespaldo"
              placeholder="Código de área + número"
              type="text"
              name="telefonoRespaldo"
              value={usuario?.telefonoRespaldo || ""}
              error={
                usuario?.telefonoRespaldo?.length > 0 && !validatePhone(usuario?.telefonoRespaldo)
              }
              onChange={handleChanges}
            />
          </Grid>
        </Grid>
      </SoftBox>
      <SoftBox
        display={{
          xs: "flex-row",
          sm: "flex",
        }}
        justifyContent="space-between"
        alignItems="center"
      >
        <SoftBox>
          <SoftBox
            display={{
              xs: "flex-row",
              sm: "flex",
            }}
            gap={2}
          >
            <SoftBox display={{ xs: "none", sm: "block" }}>
              <SoftButton
                color="primary"
                
                onClick={handleCancel}
                fullWidth={{
                  xs: true,
                  sm: false,
                }}
                disabled={loadingVerificacion}
              >
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
            </SoftBox>
            <SoftBox display={{ xs: "none", sm: "block" }}>
              <Collapse
                orientation="horizontal"
                in={usuario?.idCarrera}
                timeout={500}
                unmountOnExit
                mountOnEnter
              >
                <SoftButton
                  color="gobSecondary"
                  
                  onClick={() => {
                    setSelectedCarrera(usuario?.idCarrera);
                    setOpenModalPlan(true);
                  }}
                  sx={{
                    textWrap: "nowrap",
                  }}
                >
                  <Icon sx={{ fontWeight: "light" }}>class</Icon>
                  &nbsp;Ver info de la carrera
                </SoftButton>
              </Collapse>
            </SoftBox>
            {usuario?.idCarrera && (
              <SoftBox display={{ xs: "block", sm: "none" }}>
                <SoftButton
                  color="gobSecondary"
                  
                  onClick={() => {
                    setSelectedCarrera(usuario?.idCarrera);
                    setOpenModalPlan(true);
                  }}
                  sx={{
                    textWrap: "nowrap",
                  }}
                  fullWidth
                >
                  <Icon sx={{ fontWeight: "light" }}>class</Icon>
                  &nbsp;Ver info de la carrera
                </SoftButton>
              </SoftBox>
            )}
            <SoftBox display={{ xs: "none", sm: "block" }}>
              <Collapse
                orientation="horizontal"
                in={usuario?.idCarreraRespaldo}
                timeout={500}
                unmountOnExit
                mountOnEnter
              >
                <SoftButton
                  color="gobSecondary"
                  
                  onClick={() => {
                    setSelectedCarrera(usuario?.idCarreraRespaldo);
                    setOpenModalPlan(true);
                  }}
                  sx={{
                    textWrap: "nowrap",
                  }}
                >
                  <Icon sx={{ fontWeight: "light" }}>class</Icon>
                  &nbsp;Ver info de la carrera de respaldo
                </SoftButton>
              </Collapse>
            </SoftBox>
            {usuario?.idCarreraRespaldo && (
              <SoftBox display={{ xs: "block", sm: "none" }} mt={2}>
                <SoftButton
                  color="gobSecondary"
                  
                  onClick={() => {
                    setSelectedCarrera(usuario?.idCarreraRespaldo);
                    setOpenModalPlan(true);
                  }}
                  fullWidth
                >
                  <Icon sx={{ fontWeight: "light" }}>class</Icon>
                  &nbsp;Ver info de la carrera de respaldo
                </SoftButton>
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>
        <SoftBox
          mt={{
            xs: 2,
            sm: 0,
          }}
        >
          <SoftBox>
            <SoftButton
              color="gobSecondary"
              
              disabled={
                loadingVerificacion ||
                !usuario?.email ||
                !usuario?.nombre ||
                !usuario?.apellido ||
                !usuario?.dni ||
                !usuario?.idPais ||
                !usuario?.genero ||
                !usuario?.telefono ||
                !usuario?.idProvincia ||
                !usuario?.idLocalidad ||
                (usuario?.nombre?.length > 0 && !validarName(usuario?.nombre)) ||
                (usuario?.apellido?.length > 0 && !validarName(usuario?.apellido)) ||
                (usuario?.telefonoRespaldo && !validatePhone(usuario?.telefonoRespaldo)) ||
                (usuario?.telefono && !validatePhone(usuario?.telefono)) ||
                (usuario?.fechaNacimiento &&
                  (dayjs(usuario?.fechaNacimiento).isAfter(dayjs().subtract(17, "year")) ||
                    dayjs(usuario?.fechaNacimiento).isBefore(dayjs().subtract(100, "year")))) ||
                (usuario?.email && !validateEmail(usuario?.email)) ||
                (usuario?.dni && !validateDNI(usuario?.dni))
              }
              fullWidth={{
                xs: true,
                sm: false,
              }}
              onClick={() => {
                sendVerificacion({ variables: { email: usuario?.email } })
                  .then(() => {
                    setOpenVerificar(true);
                  })
                  .catch((e) => {
                    handleSnackbar(e.message, "error");
                  });
              }}
            >
              {loadingVerificacion ? (
                <CircularProgress
                  size={15}
                  sx={{
                    marginRight: 0.5,
                  }}
                  color="inherit"
                />
              ) : (
                <Icon sx={{ fontWeight: "light" }}>done</Icon>
              )}
              &nbsp;{loadingVerificacion ? "Enviando correo" : "Crear cuenta"}
            </SoftButton>
          </SoftBox>
        </SoftBox>
        <ModalVerificar
          open={openVerificar}
          handleClose={() => setOpenVerificar(false)}
          usuario={usuario}
          handleCancel={handleCancel}
          handleResend={() => {
            sendVerificacion({ variables: { email: usuario?.email } })
              .then(() => {
                handleSnackbar("Correo de verificación reenviado", "success");
              })
              .catch((e) => {
                handleSnackbar(e.message || "Error al enviar el correo electrónico", "error");
              });
          }}
        />
      </SoftBox>
    </SoftBox>
  );
}

CrearCuenta.defaultProps = {
  handleCancel: () => {},
};

CrearCuenta.propTypes = {
  handleCancel: PropTypes.func,
};

export default CrearCuenta;
